import {
	faBuilding,
	faCalendarDays,
	faChartLine,
	faFileInvoiceDollar,
	faFileLines,
	faFolderTree,
	faHome,
	faTags,
	faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
	CheckboxAtom,
	DropdownAtom,
	MultiSelectAtom,
} from "../../../components/atoms/atom";
import {
	CATEGORY_ACCESS,
	CATEGORY_ACCESS_LEVELS,
	OVERVIEW_LEVEL,
	OVERVIEW_LEVELS,
	PERMISSION_AREA_CONFIG,
	PROJECT_ACTION,
	TICKET_ACCESS_LEVEL,
	TICKET_EDIT_LEVELS,
	TICKET_VIEW_LEVELS,
	actionLabelKey,
	createDefaultPermissions,
	getCategoryAccessScope,
	getSelectedCategoryIds,
	sanitizePermissions,
} from "../../../constant/projectPermissions";
import { useGetProjectTicketCategoriesQuery } from "../../../store/services/ticketCategoriesService";
import "./permissions-form.scss";

const CATEGORY_SCOPE_SCOPED = "SCOPED";

const AREA_ICONS = {
	overview: faHome,
	planning: faCalendarDays,
	costTracking: faChartLine,
	lv: faFileLines,
	invoice: faFileInvoiceDollar,
	ticketCategories: faFolderTree,
	tickets: faTags,
	members: faUsers,
	companies: faBuilding,
};

function PermissionsForm({
	projectId,
	permissions,
	onChange,
	disabled = false,
	showCategoryAccess = true,
	compactMatrix = true,
}) {
	const { t } = useTranslation();
	const { data: categoriesResponse } = useGetProjectTicketCategoriesQuery(
		{ projectId },
		{ skip: !projectId },
	);
	const ticketCategories = useMemo(
		() => categoriesResponse?.items ?? [],
		[categoriesResponse?.items],
	);

	const effectivePermissions = useMemo(
		() => permissions ?? createDefaultPermissions(),
		[permissions],
	);

	const categoryScopeOptions = useMemo(
		() => [
			{
				value: CATEGORY_ACCESS_LEVELS[0],
				label: t("projects.permissions.category-access-all"),
			},
			{
				value: CATEGORY_SCOPE_SCOPED,
				label: t("projects.permissions.category-access-specific"),
			},
			{
				value: CATEGORY_ACCESS_LEVELS[1],
				label: t("projects.permissions.category-access-none"),
			},
		],
		[t],
	);

	const categorySelectOptions = useMemo(
		() =>
			ticketCategories.map((cat) => ({
				value: cat.id,
				label: cat.name,
			})),
		[ticketCategories],
	);

	const selectedCategoryOptions = useMemo(() => {
		const ids = getSelectedCategoryIds(
			effectivePermissions.ticketCategories?.categoryAccess,
		);
		return categorySelectOptions.filter((opt) => ids.includes(opt.value));
	}, [effectivePermissions, categorySelectOptions]);

	if (!permissions) {
		return null;
	}

	const permissionsReadOnly = disabled;

	// Every mutation is run through sanitizePermissions so the view-dependency
	// invariant holds live: switching an area's `view` off immediately clears its
	// dependent actions (which also render disabled), and the form can never emit
	// an incoherent set. The same sanitizer guards the save boundary and backend.
	const setAreaValue = (area, action, value) => {
		if (permissionsReadOnly || !permissions) return;
		onChange(
			sanitizePermissions({
				...permissions,
				[area]: {
					...permissions[area],
					[action]: value,
				},
			}),
		);
	};

	const updateTicketCategories = (patch) => {
		if (permissionsReadOnly || !permissions) return;
		onChange(
			sanitizePermissions({
				...permissions,
				ticketCategories: {
					...permissions.ticketCategories,
					...patch,
				},
			}),
		);
	};

	const handleCategoryScopeChange = (scope) => {
		if (scope === CATEGORY_ACCESS_LEVELS[0]) {
			updateTicketCategories({ categoryAccess: CATEGORY_ACCESS.ALL });
			return;
		}
		if (scope === CATEGORY_ACCESS_LEVELS[1]) {
			updateTicketCategories({ categoryAccess: CATEGORY_ACCESS.NONE });
			return;
		}
		const current = getSelectedCategoryIds(
			permissions.ticketCategories?.categoryAccess,
		);
		const fallback =
			current.length > 0
				? current
				: ticketCategories.map((cat) => cat.id);
		updateTicketCategories({ categoryAccess: fallback });
	};

	const handleCategoryMultiChange = (selected) => {
		const ids = (selected ?? []).map((opt) => opt.value);
		updateTicketCategories({ categoryAccess: ids });
	};

	const renderCategoryAccessControls = () => {
		if (!effectivePermissions.ticketCategories?.view) {
			return null;
		}

		const scope = getCategoryAccessScope(
			effectivePermissions.ticketCategories?.categoryAccess,
		);

		return (
			<>
				<div className="permissions-form__row">
					<span className="permissions-form__row-label">
						{t("projects.permissions.category-access-scope")}
					</span>
					<DropdownAtom
						className="permissions-form__row-select"
						value={scope}
						options={categoryScopeOptions}
						onChange={(e) =>
							handleCategoryScopeChange(e.target.value)
						}
						disabled={permissionsReadOnly}
						sortOptions={false}
						isSearchable={false}
					/>
				</div>
				{scope === CATEGORY_SCOPE_SCOPED && (
					<div className="permissions-form__row permissions-form__row--stack">
						{ticketCategories.length === 0 ? (
							<p className="permissions-form__cat-empty text-muted">
								{t(
									"projects.permissions.category-access-empty",
								)}
							</p>
						) : (
							<>
								<MultiSelectAtom
									label={t(
										"projects.permissions.category-access-selected",
									)}
									placeholder={t(
										"projects.permissions.category-access-placeholder",
									)}
									options={categorySelectOptions}
									selectedOption={selectedCategoryOptions}
									value={selectedCategoryOptions}
									onChange={handleCategoryMultiChange}
									isDisabled={permissionsReadOnly}
									closeMenuOnSelect={false}
								/>
								{selectedCategoryOptions.length === 0 && (
									<p className="permissions-form__cat-error">
										{t(
											"projects.permissions.category-access-required",
										)}
									</p>
								)}
							</>
						)}
					</div>
				)}
			</>
		);
	};

	const ticketViewOptions = TICKET_VIEW_LEVELS.map((value) => ({
		value,
		label: t(`projects.permissions.ticket-view.${value}`),
	}));

	const ticketEditOptions = TICKET_EDIT_LEVELS.map((value) => ({
		value,
		label: t(`projects.permissions.ticket-edit.${value}`),
	}));

	const overviewLevelOptions = OVERVIEW_LEVELS.map((value) => ({
		value,
		label: t(`projects.permissions.overview-level.${value}`),
	}));

	const matrixClassName = [
		"permissions-form__matrix",
		compactMatrix && "permissions-form__matrix--compact",
	]
		.filter(Boolean)
		.join(" ");

	return (
		<div className="permissions-form">
			<div className={matrixClassName}>
				{PERMISSION_AREA_CONFIG.map((config) => {
					const icon = AREA_ICONS[config.area];
					const areaState = effectivePermissions[config.area];
					// `view` is the area's access gate. Its dependent actions are
					// only meaningful once view is granted, so we disable (grey)
					// them until then. tickets gates on view !== NONE (a level);
					// boolean-view areas gate on view === true; areas without a
					// view action (overview, …) are always "open".
					const hasBooleanView = config.booleanActions?.includes(
						PROJECT_ACTION.VIEW,
					);
					const areaViewEnabled = config.ticketLevels
						? areaState?.view !== TICKET_ACCESS_LEVEL.NONE
						: hasBooleanView
							? areaState?.view === true
							: true;
					const hasDependents =
						!!config.ticketLevels ||
						(hasBooleanView &&
							config.booleanActions.length > 1);
					const showViewHint =
						hasDependents && !areaViewEnabled;
					return (
						<section
							key={config.area}
							className="permissions-form__psect"
						>
							<header className="permissions-form__psect-hdr">
								{icon && (
									<FontAwesomeIcon
										icon={icon}
										className="permissions-form__psect-icon"
										fixedWidth
									/>
								)}
								<span>{t(config.labelKey)}</span>
							</header>
							<div className="permissions-form__psect-body">
								{config.ticketLevels && (
									<>
										<div className="permissions-form__row permissions-form__row--master">
											<span className="permissions-form__row-label">
												{t(
													"projects.permissions.ticket-view-label",
												)}
											</span>
											<DropdownAtom
												className="permissions-form__row-select"
												value={
													effectivePermissions
														.tickets?.view ??
													TICKET_ACCESS_LEVEL.NONE
												}
												options={ticketViewOptions}
												onChange={(e) =>
													setAreaValue(
														"tickets",
														"view",
														e.target.value,
													)
												}
												disabled={permissionsReadOnly}
												sortOptions={false}
												isSearchable={false}
											/>
										</div>
										<div
											className={
												areaViewEnabled
													? "permissions-form__row"
													: "permissions-form__row permissions-form__row--locked"
											}
										>
											<span className="permissions-form__row-label">
												{t(
													"projects.permissions.ticket-edit-label",
												)}
											</span>
											<DropdownAtom
												className="permissions-form__row-select"
												value={
													effectivePermissions
														.tickets?.edit ??
													TICKET_ACCESS_LEVEL.NONE
												}
												options={ticketEditOptions}
												onChange={(e) =>
													setAreaValue(
														"tickets",
														"edit",
														e.target.value,
													)
												}
												disabled={
													permissionsReadOnly ||
													!areaViewEnabled
												}
												sortOptions={false}
												isSearchable={false}
											/>
										</div>
									</>
								)}

								{config.overviewLevels && (
									<div className="permissions-form__row">
										<span className="permissions-form__row-label">
											{t(
												"projects.permissions.overview-view-label",
											)}
										</span>
										<DropdownAtom
											className="permissions-form__row-select"
											value={
												effectivePermissions.overview
													?.view ?? OVERVIEW_LEVEL.OVERVIEW
											}
											options={overviewLevelOptions}
											onChange={(e) =>
												setAreaValue(
													"overview",
													"view",
													e.target.value,
												)
											}
											disabled={permissionsReadOnly}
											sortOptions={false}
											isSearchable={false}
										/>
									</div>
								)}

								{config.booleanActions?.map((action) => {
									const isViewAction =
										hasBooleanView &&
										action === PROJECT_ACTION.VIEW;
									// Treat `view` as the master only when it
									// actually gates dependents (members/companies
									// expose just `view`, so it renders plain).
									const isViewMaster =
										isViewAction && hasDependents;
									// Dependents (every action but `view`) are
									// locked until the area's view is granted; the
									// view switch itself is never locked.
									const isDependentDisabled =
										!isViewAction && !areaViewEnabled;
									const rowClassName = [
										"permissions-form__row",
										isViewMaster &&
											"permissions-form__row--master",
										isDependentDisabled &&
											"permissions-form__row--locked",
									]
										.filter(Boolean)
										.join(" ");
									return (
										<div
											className={rowClassName}
											key={`${config.area}-${action}`}
										>
											<label
												className="permissions-form__row-label"
												htmlFor={`perm-${config.area}-${action}`}
											>
												{t(actionLabelKey(action))}
											</label>
											<CheckboxAtom
												type="switch"
												id={`perm-${config.area}-${action}`}
												checked={
													effectivePermissions[
														config.area
													]?.[action] === true
												}
												onChange={(e) =>
													setAreaValue(
														config.area,
														action,
														e.target.checked,
													)
												}
												disabled={
													permissionsReadOnly ||
													isDependentDisabled
												}
											/>
										</div>
									);
								})}

								{showViewHint && (
									<p className="permissions-form__hint">
										{t(
											"projects.permissions.view-required-hint",
										)}
									</p>
								)}

								{config.categoryAccess &&
									showCategoryAccess &&
									renderCategoryAccessControls()}
							</div>
						</section>
					);
				})}
			</div>
		</div>
	);
}

export default PermissionsForm;
